import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MainLayout } from '../../../../components/MainLayout';
import { PagesHeader } from '../../../../components/shared/PagesHeader';
import { useTranslation } from 'react-i18next';
import { ROUTES } from './AddNewBillingAgreement.config';
import { Formik } from 'formik';
import { addNewBillingAgreementInitialValues } from '../../config/initialValues';
import { addNewBillingAgreementValidationSchema } from '../../config/validation';
import {
  addNewBillingAgreementFormConfig,
  termsAndConditionsFieldConfig,
} from './formConfig';
import styles from './AddNewBillingAgreement.styles.scss';
import BillingAgreementForm from '../../components/BillingAgreementForm/BillingAgreementForm';
import AddNewBillingAgreementDialog from '../../components/AddNewBillingAgreementDialog/AddNewBillingAgreementDialog';
import { Icon } from '../../../../components/shared/Icon';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import { Button } from 'primereact/button';
import {
  createSpecialAgreement,
  getSpecialAgreementDrafts,
  getVatInfo,
} from '../../../../services/apis';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ACTING_AS, THIRD_PARTY_REGISTRATION } from '../../config/constants';
import { AddNewBillingTerms } from '../../components/AddNewBillingTerms/AddNewBillingTerms';
import useUnsavedChangesGuard from '../../../../hooks/useUnsavedChangesGuard';
import DraftBillingDialog from '../../components/DraftBillingDialog/DraftBillingDialog';
import {
  mapSpecialBillingAgreementToFormValues,
  uploadFiles,
} from '../../../../helpers/specialBillingHelpers';

const AddNewBillingAgreement = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [showTerms, setShowTerms] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [addToTable, setAddToTable] = useState(false);
  const [isUpdateValue, setIsUpdateValue] = useState(-1);
  const [agreements, setAgreements] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const formRef = useRef();

  const { userInfo } = useAuthentication();

  const isDirtyFn = useCallback(
    () => formRef.current?.dirty || agreements.length > 0,
    [agreements],
  );
  const { guardNavigation } = useUnsavedChangesGuard(
    isDirtyFn,
    t('special_billing.unsaved_changes_message'),
  );

  const [formInitValues, setFormInitValues] = useState(
    addNewBillingAgreementInitialValues({}),
  );

  const { agreementType, createAs, referenceNumber } = location.state || {};

  const removeAgreement = (agreementIndex) => {
    setAgreements((prev) => prev.filter((_, i) => i !== agreementIndex));
  };

  const editAgreement = (agreement, index) => {
    setIsUpdateValue(index);
    formRef.current.setValues(agreement);
  };

  const onSubmit = () => {
    return createSpecialAgreement(
      agreementType,
      createAs,
      agreements,
      referenceNumber,
    );
  };

  const onSaveDraft = (draftAgreements) => {
    return createSpecialAgreement(
      agreementType,
      createAs,
      draftAgreements,
      referenceNumber,
      'Draft',
    );
  };

  useEffect(() => {
    if (!location.state) {
      navigate(`/${i18n.language}/additional-functionalities/special-billing`);
      return;
    }

    // Only set initial values if not loading from draft
    if (!params.id && userInfo?.tax_number) {
      getVatInfo(userInfo.tax_number)
        .then(({ data }) => {
          const vatName = data?.name ?? '';
          const vatPhone = data?.phoneNumber ?? '';
          const vatEmail = data?.email ?? '';

          setFormInitValues(
            addNewBillingAgreementInitialValues({
              sellerName: createAs === ACTING_AS.SELLER ? vatName : '',
              sellerVatNumber:
                createAs === ACTING_AS.SELLER ? userInfo.tax_number : '',
              sellerPhoneNumber: createAs === ACTING_AS.SELLER ? vatPhone : '',
              sellerEmail: createAs === ACTING_AS.SELLER ? vatEmail : '',
              buyerName: createAs === ACTING_AS.BUYER ? vatName : '',
              buyerVatNumber:
                createAs === ACTING_AS.BUYER ? userInfo.tax_number : '',
              buyerPhoneNumber: createAs === ACTING_AS.BUYER ? vatPhone : '',
              buyerEmail: createAs === ACTING_AS.BUYER ? vatEmail : '',
              thirdPartyName: createAs === ACTING_AS.THIRD ? vatName : '',
              thirdPartyRegistered: THIRD_PARTY_REGISTRATION.VAT_REGISTERED,
              thirdPartyVatNumber:
                createAs === ACTING_AS.THIRD ? userInfo.tax_number : '',
              thirdPartyPhoneNumber:
                createAs === ACTING_AS.THIRD ? vatPhone : '',
              thirdPartyEmail: createAs === ACTING_AS.THIRD ? vatEmail : '',
            }),
          );
        })
        .catch(() => {
          setFormInitValues(
            addNewBillingAgreementInitialValues({
              sellerName:
                createAs === ACTING_AS.SELLER ? userInfo.firstName : '',
              sellerVatNumber:
                createAs === ACTING_AS.SELLER ? userInfo.tax_number : '',
              sellerPhoneNumber:
                createAs === ACTING_AS.SELLER ? userInfo.phoneNumber : '',
              sellerEmail: createAs === ACTING_AS.SELLER ? userInfo.email : '',
              buyerName: createAs === ACTING_AS.BUYER ? userInfo.firstName : '',
              buyerVatNumber:
                createAs === ACTING_AS.BUYER ? userInfo.tax_number : '',
              buyerPhoneNumber:
                createAs === ACTING_AS.BUYER ? userInfo.phoneNumber : '',
              buyerEmail: createAs === ACTING_AS.BUYER ? userInfo.email : '',
              thirdPartyName:
                createAs === ACTING_AS.THIRD ? userInfo.firstName : '',
              thirdPartyRegistered: THIRD_PARTY_REGISTRATION.VAT_REGISTERED,
              thirdPartyVatNumber:
                createAs === ACTING_AS.THIRD ? userInfo.tax_number : '',
              thirdPartyPhoneNumber:
                createAs === ACTING_AS.THIRD ? userInfo.phoneNumber : '',
              thirdPartyEmail:
                createAs === ACTING_AS.THIRD ? userInfo.email : '',
            }),
          );
        });
    }
  }, [userInfo, createAs, params.id, navigate, location.state]);

  // Separate effect for loading draft data
  useEffect(() => {
    if (params.id && referenceNumber) {
      setShowTerms(false);
      getSpecialAgreementDrafts(referenceNumber)
        .then(({ data }) => {
          const reversedAgreements = data.map((agreement) =>
            mapSpecialBillingAgreementToFormValues(
              agreement,
              {},
              agreementType,
            ),
          );
          if (reversedAgreements.length === 1) {
            setFormInitValues(reversedAgreements[0]);
          } else {
            setAgreements(reversedAgreements);
            setAddToTable(true);
          }
          setIsDraft(true);
        })
        .catch((e) => {
          console.error('Error loading draft agreement', e);
          navigate('..');
        });
    }
  }, [params.id, referenceNumber, navigate]);

  return (
    <MainLayout className={styles.reportFailure} eInvoicing={true}>
      <PagesHeader
        withBreadcrumb={true}
        routes={ROUTES({ agreementType, createAs })}
      />
      {showTerms && (
        <AddNewBillingTerms
          onConfirm={setShowTerms}
          agreementType={agreementType}
        />
      )}
      {!showTerms && (
        <div className={styles.container}>
          <Formik
            innerRef={formRef}
            validateOnMount
            enableReinitialize
            validationSchema={addNewBillingAgreementValidationSchema({
              t,
              createAs,
              agreementType,
            })}
            initialValues={formInitValues}
            onSubmit={async (values, { setFieldValue }) => {
              const isUploadSuccess = await uploadFiles(values, setFieldValue);
              if (!isUploadSuccess) {
                return;
              }
              if (values.isTable) {
                if (isUpdateValue > -1) {
                  setAgreements((prev) => {
                    const updatedAgreements = [...prev];
                    updatedAgreements[isUpdateValue] = { ...values };
                    return updatedAgreements;
                  });
                  setIsUpdateValue(-1);
                } else {
                  setAgreements((prev) => [...prev, values]);
                }
                setAddToTable(true);
                formRef.current.resetForm();
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isValid,
              dirty,
              setFieldValue,
              setTouched,
              setValues,
              submitForm,
            }) => {
              if (
                values.thirdPartyRegistered ==
                  THIRD_PARTY_REGISTRATION.VAT_REGISTERED &&
                values.thirdPartyTinNumber
              ) {
                setFieldValue('thirdPartyTinNumber', '');
              } else if (
                values.thirdPartyRegistered ==
                  THIRD_PARTY_REGISTRATION.NOT_VAT_REGISTERED &&
                values.thirdPartyVatNumber
              ) {
                setFieldValue('thirdPartyVatNumber', '');
              }

              return (
                <BillingAgreementForm
                  isDraft={isDraft}
                  isUpdateValue={isUpdateValue}
                  values={values}
                  errors={errors}
                  isValid={isValid}
                  touched={touched}
                  createAs={createAs}
                  agreementType={agreementType}
                  setAddToTable={setAddToTable}
                  addToTable={addToTable}
                  setAgreements={setAgreements}
                  agreements={agreements}
                  submitForm={submitForm}
                  setValues={setValues}
                  removeAgreement={removeAgreement}
                  editAgreement={editAgreement}
                  formConfig={addNewBillingAgreementFormConfig({
                    t,
                    lang: i18n.language,
                    values,
                    setTouched,
                    setFieldValue,
                    createAs,
                    agreementType,
                  })}
                  termsAndConditionsFieldConfig={termsAndConditionsFieldConfig({
                    t,
                    lang: i18n.language,
                    values,
                    setTouched,
                    setFieldValue,
                    agreementType,
                  })}
                  actionButtons={
                    <>
                      <Button
                        link
                        type="button"
                        className={'gap-2 color-theme-primary'}
                        onClick={() => guardNavigation(() => navigate(-1))}
                      >
                        <Icon name="leftArrow" className={styles.backArrow} />
                        {t('common.back')}
                      </Button>
                      <Button
                        label={t('special_billing.save-as-draft')}
                        outlined
                        size="medium"
                        className={styles.formBtn}
                        type="button"
                        onClick={async () => {
                          const handleDraftSuccess = () => {
                            setShowDraftDialog(true);
                          };
                          if (addToTable) {
                            onSaveDraft(agreements).then(handleDraftSuccess);
                          } else if (isValid) {
                            const isUploadSuccess = await uploadFiles(
                              values,
                              setFieldValue,
                            );
                            if (!isUploadSuccess) {
                              return;
                            }
                            setAgreements([values]);
                            onSaveDraft([values]).then(handleDraftSuccess);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={async () => {
                          const isUploadSuccess = await uploadFiles(
                            values,
                            setFieldValue,
                          );
                          if (!isUploadSuccess) {
                            return;
                          }
                          if (addToTable) {
                            guardNavigation(() => setShowDialog(true));
                          } else if (isValid) {
                            setAgreements([values]);
                            setShowDialog(true);
                          }
                        }}
                        size="medium"
                        className={styles.formBtn}
                        label={t('common.submit')}
                        disabled={
                          (!addToTable && Object.keys(errors).length > 0) ||
                          (addToTable && agreements.length === 0) ||
                          !values.termsAndConditions
                        }
                      />
                    </>
                  }
                />
              );
            }}
          </Formik>
          <AddNewBillingAgreementDialog
            agreementType={agreementType}
            isOpen={showDialog}
            setIsOpen={setShowDialog}
            onSubmit={onSubmit}
          />
          <DraftBillingDialog
            agreementType={agreementType}
            isOpen={showDraftDialog}
            setIsOpen={setShowDraftDialog}
          />
        </div>
      )}
    </MainLayout>
  );
};

export default AddNewBillingAgreement;
